import { useParams, useNavigate } from "react-router-dom";
import logo from "assets/images/logoSvg.svg";
import { routePath } from "config/routeMgt/routePath";
import { Navigate, useLocation } from "react-router-dom";
import "../assets/style.css";
import { SignInForm } from "../components/SignInForm";
import { useIsAuthenticated } from "react-auth-kit";
import { useState, useEffect } from "react";
import googleLogo from "assets/images/googleIcon.png";
import { END_POINT } from "config/environment";
import { BeatLoader } from "react-spinners";
import { CloseOutlined } from "@ant-design/icons";

type LocationState = {
  from?: {
    pathname?: string;
  };
};

export const SignIn = () => {
  const navigate = useNavigate();
  const isAuthenticated = useIsAuthenticated();
  const { type } = useParams();
  const location = useLocation();

  const [isLoading, setIsLoading] = useState(true);
  const [step, setStep] = useState(1);
  const [oauthLoading, setOauthLoading] = useState(false);
  const [showCookieBanner, setShowCookieBanner] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    console.log("Checking cookie consent...");
    const consent = localStorage.getItem("cookieConsent");
    console.log("Cookie consent value:", consent);

    if (!consent) {
      console.log("No consent found, showing banner");
      setShowCookieBanner(true);
    } else {
      console.log("Consent found, hiding banner");
      setShowCookieBanner(false);
    }
  }, []);

  const getDefaultAccountType = () => {
    if (type === "recruiter") return "recruiter";
    if (type === "talent") return "talent";

    const savedType = localStorage.getItem("lastAccountType");
    if (savedType === "recruiter" || savedType === "talent") {
      return savedType;
    }

    return "talent";
  };

  const defaultAccountType = getDefaultAccountType();

  useEffect(() => {
    if (type === "recruiter" || type === "talent") {
      return;
    }

    if (window.location.pathname === "/sign-in") {
      const savedType = localStorage.getItem("lastAccountType");
      const defaultType = savedType === "recruiter" ? "recruiter" : "talent";
      navigate(`/sign-in/${defaultType}`, { replace: true });
    }
  }, [type, navigate]);

  const from = location.state?.from?.pathname || routePath.home;

  if (isAuthenticated()) {
    return <Navigate to={from} replace={true} />;
  }

  const handleNext = () => {
    setStep(2);
  };

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
    }
  };

  const redirectToGoogleAuth = async () => {
    setOauthLoading(true);
    try {
      const isCorporate = defaultAccountType === "recruiter" ? 1 : 0;
      const response = await fetch(
        `${END_POINT.AUTH_URL}/auth?type=${defaultAccountType}&is_corporate=${isCorporate}`,
      );
      const data = await response.json();
      window.location.assign(data.url);
    } catch (error) {
      console.error("Google sign in failed:", error);
      setOauthLoading(false);
    }
  };

  const handleCookieAccept = () => {
    console.log("Accepting cookies");
    localStorage.setItem("cookieConsent", "accepted");
    setShowCookieBanner(false);
  };

  const handleCookieOptOut = () => {
    console.log("Opting out of cookies");
    localStorage.setItem("cookieConsent", "opted-out");
    setShowCookieBanner(false);
  };

  const handleResetCookieConsent = () => {
    console.log("Resetting cookie consent");
    localStorage.removeItem("cookieConsent");
    setShowCookieBanner(true);
  };

  return (
    <>
      {isLoading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 h-screen overflow-auto">
          <div className="Container py-3 bg-white flex flex-col h-full">
            <div className="mt-2 shimmer-wrapper">
              <div className="shimmer-text" style={{ width: "150px" }}></div>
              <div className="shimmer-text" style={{ width: "200px" }}></div>
            </div>
            <div className="flex justify-center mt-10 shimmer-wrapper">
              <div className="authFormWrap">
                <div className="shimmer-text" style={{ width: "200px" }}></div>
                <div className="shimmer-text" style={{ width: "100%" }}></div>
              </div>
            </div>
          </div>
          <div className="bg-primary h-screen Container lg:flex items-center justify-center hidden w-full sticky top-0">
            <div className="shimmer-effect max-h-96 w-full"></div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 h-screen overflow-auto">
          <div className="Container py-3 bg-white flex flex-col h-full">
            <div className="mt-2">
              <a
                href="https://www.arnopro.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={logo} alt="logo" />
              </a>
            </div>
            <div className="flex justify-center mt-10 flex-1">
              <div className="w-full max-w-md">
                {/* Progress */}
                <div className="flex gap-2 mb-8">
                  <div
                    className={`h-1 flex-1 rounded-full ${step >= 1 ? "bg-gray-900" : "bg-gray-200"}`}
                  />
                  <div
                    className={`h-1 flex-1 rounded-full ${step >= 2 ? "bg-gray-900" : "bg-gray-200"}`}
                  />
                </div>

                {step === 2 && (
                  <button
                    onClick={handleBack}
                    className="text-sm text-gray-500 mb-6 flex items-center gap-1"
                  >
                    ← Back
                  </button>
                )}

                {/* Badge */}
                <div className="mb-4">
                  <span className="inline-flex items-center gap-2 text-sm bg-gray-100 text-gray-700 px-3 py-1.5 rounded-full">
                    ⭐ Signing in as{" "}
                    {defaultAccountType === "recruiter"
                      ? "Recruiter"
                      : "Talent"}
                  </span>
                </div>

                <div className="space-y-5">
                  {step === 1 && (
                    <>
                      {/* Title */}
                      <div className="mb-8">
                        <h2 className="text-3xl font-semibold text-gray-900 mb-3">
                          {defaultAccountType === "recruiter"
                            ? "Hire smarter and faster"
                            : "Get hired for roles that fit you"}
                        </h2>

                        <p className="text-gray-500 text-base leading-relaxed">
                          {defaultAccountType === "recruiter"
                            ? "Use a smarter hiring experience designed to help you find the best talents faster."
                            : "Get matched to opportunities that fit your skills, values, and growth goals — not just keywords."}
                        </p>
                      </div>

                      {/* Features */}
                      <div className="space-y-4 mb-8">
                        {(defaultAccountType === "recruiter"
                          ? [
                              "Find talents based on skills, potential, and the qualities that truly matter.",
                              "Manage recruitment with ease, from sourcing candidates to making the perfect hire.",
                              "Build high-performing teams that help your business scale and succeed.",
                            ]
                          : [
                              "Smarter matches. Better opportunities.",
                              "Connect with the right employers",
                              "Improve your chances of getting hired",
                            ]
                        ).map((text, i) => (
                          <div
                            key={i}
                            className="flex items-center gap-3 border border-gray-200 rounded-xl px-4 py-4 bg-gray-50"
                          >
                            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-blue-100 text-blue-600">
                              ✓
                            </div>
                            <span className="text-gray-700 text-sm">
                              {text}
                            </span>
                          </div>
                        ))}
                      </div>

                      {/* CTA */}
                      <button
                        type="button"
                        onClick={handleNext}
                        className="w-full bg-primary text-white py-3.5 rounded-xl font-medium mb-4 mt-10"
                      >
                        Sign in
                      </button>

                      <button
                        onClick={redirectToGoogleAuth}
                        disabled={oauthLoading}
                        className="w-full border border-gray-300 py-3 rounded-xl flex items-center justify-center gap-3 hover:bg-gray-50"
                      >
                        <img
                          src={googleLogo}
                          alt="Google"
                          className="w-5 h-5"
                        />
                        <span>
                          {oauthLoading ? (
                            <BeatLoader size={8} />
                          ) : (
                            "Sign in with Google"
                          )}
                        </span>
                      </button>
                    </>
                  )}

                  {step === 2 && (
                    <>
                      {/* Title */}
                      <div className="mb-6">
                        <h2 className="text-3xl font-semibold text-gray-900 mb-2">
                          Good to see you again
                        </h2>
                        <p className="text-gray-500 text-base">
                          {defaultAccountType === "recruiter"
                            ? "Start finding top talent for your team today."
                            : "Sign in to find your next role."}
                        </p>
                      </div>

                      <SignInForm defaultAccountType={defaultAccountType} />
                    </>
                  )}
                </div>

                <div className="text-center text-sm mt-6">
                  <span className="text-gray-500">Don't have an account? </span>
                  <div className="text-center text-sm mt-6">
                    <span className="text-gray-500">
                      Don't have an account?{" "}
                    </span>
                    <button
                      onClick={() => {
                        navigate(`/sign-up/${defaultAccountType}`);
                      }}
                      className="text-primary font-medium"
                    >
                      Sign up as{" "}
                      {defaultAccountType === "recruiter"
                        ? "Recruiter"
                        : "Talent"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="text-sm flex items-center justify-center gap-16 pt-12 mt-auto pb-12">
              <a
                href="https://www.arnopro.com/arno-terms-of-use"
                className="underline"
                target="_blank"
                rel="noreferrer"
                aria-label="Terms and Conditions"
              >
                Terms & Conditions
              </a>
              <a
                href="https://www.arnopro.com/arno-data-privacy-policy"
                className="underline"
                target="_blank"
                rel="noreferrer"
                aria-label="Privacy Policy"
              >
                Privacy Policy
              </a>
              <a
                href="https://www.arnopro.com/arno-cookies-policy"
                className="underline"
                target="_blank"
                rel="noreferrer"
                aria-label="Cookies Policy"
              >
                Cookies Policy
              </a>
            </div>
          </div>
          <div className="bg-primary h-screen Container lg:flex items-center justify-center hidden w-full sticky top-0">
            <video
              src="/assets/authVideo.mp4"
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      )}

      {showCookieBanner && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/30 backdrop-blur-sm">
          <div className="w-full max-w-4xl mx-4 mb-8 animate-slide-up">
            <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-6 sm:p-8">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-6">

                <div className="flex-shrink-0 hidden sm:flex items-center justify-center w-14 h-14 bg-blue-50 rounded-full">
                  <svg
                    className="w-8 h-8 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"
                    />
                  </svg>
                </div>

                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    🍪 Cookie Preferences
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    We use cookies to enhance your browsing experience, analyze
                    site traffic, and personalize content. By clicking "Accept
                    All", you consent to our use of cookies in accordance with
                    our{" "}
                    <a
                      href="https://www.arnopro.com/arno-terms-of-use"
                      className="text-primary font-medium hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Terms & Conditions
                    </a>
                    ,{" "}
                    <a
                      href="https://www.arnopro.com/arno-data-privacy-policy"
                      className="text-primary font-medium hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Privacy Policy
                    </a>
                    , and{" "}
                    <a
                      href="https://www.arnopro.com/arno-cookies-policy"
                      className="text-primary font-medium hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Cookies Policy
                    </a>
                    . You can manage your preferences at any time.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto flex-shrink-0">
                  <button
                    onClick={handleCookieAccept}
                    className="px-8 py-2.5 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-colors shadow-sm hover:shadow-md whitespace-nowrap"
                  >
                    Accept All
                  </button>
                  <button
                    onClick={handleCookieOptOut}
                    className="px-6 py-2.5 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors whitespace-nowrap"
                  >
                    Opt Out
                  </button>
                </div>

                <button
                  onClick={handleCookieOptOut}
                  className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors md:hidden"
                >
                  <CloseOutlined className="text-lg" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .animate-slide-up {
          animation: slideUp 0.4s ease-out forwards;
        }
      `}</style>
    </>
  );
};
